import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '../../components/PageLayout';
import { PageLoader } from '../../components/PageLoader/PageLoader';
import { Button } from '../../components/Button';
import { Alert } from '../../components/Alert/Alert';
import { Paragraph } from '../../components/Paragraph';
import { Label } from '../../components/Label/Label';
import { ContainerCard } from '../../components/ContainerCard';
import { toast } from 'react-toastify';
import { useHandleSlackOAuthCallback } from '../../hooks/mutations/useHandleSlackOAuthCallback';
import { useInstallSlackChannel } from '../../hooks/mutations/useInstallSlackChannel';
import { Routes } from '../../enums/Routes';

const TOAST_POSITION = 'bottom-right' as const;
import classes from './SlackOAuthCallbackPage.module.css';

interface Channel {
  id: string;
  name: string;
  isPrivate: boolean;
}

const TEAM_ID_KEY = 'slack_oauth_teamId';
const CONTEXT_KEY = 'slack_oauth_context';

export const SlackOAuthCallbackPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCalledCallbackRef = useRef(false);

  const teamId = sessionStorage.getItem(TEAM_ID_KEY);
  const context = sessionStorage.getItem(CONTEXT_KEY);
  const isCreateSurvey = context === 'create-survey';
  const backUrl = isCreateSurvey
    ? Routes.CreateSurvey
    : teamId ? `/teams/${teamId}/integration` : '/settings';

  const { mutate: handleCallback, error: callbackError } = useHandleSlackOAuthCallback({
    onSuccess: (data) => {
      setChannels(data.channels);
      setWorkspaceName(data.workspaceName);
      setWorkspaceId(data.slackWorkspaceId);
      setIsLoading(false);
    },
    onError: () => {
      toast.error(t('slackOAuthCallback.couldNotConnect'), {
        position: TOAST_POSITION,
      });
      setIsLoading(false);
    },
  });
  const { mutate: installChannel, isPending: isInstalling } = useInstallSlackChannel();

  useEffect(() => {
    if (hasCalledCallbackRef.current) return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      hasCalledCallbackRef.current = true;
      const errorMessage = error === 'access_denied'
        ? t('slackOAuthCallback.canceledConnection')
        : t('slackOAuthCallback.couldNotConnect');
      toast.error(errorMessage, { position: TOAST_POSITION });
      setIsLoading(false);

      const redirectUrl = backUrl;

      sessionStorage.removeItem(TEAM_ID_KEY);
      sessionStorage.removeItem(CONTEXT_KEY);
      setTimeout(() => navigate(redirectUrl), 2000);
      return;
    }

    if (!code || !state) {
      setIsLoading(false);
      return;
    }

    hasCalledCallbackRef.current = true;
    handleCallback({ code, state });
  }, []);

  const handleInstall = () => {
    if (!selectedChannel || !workspaceId || !teamId) return;

    installChannel(
      {
        trustpulseTeamId: Number(teamId),
        slackChannelId: selectedChannel,
        slackWorkspaceId: workspaceId,
      },
      {
        onSuccess: (data) => {
          sessionStorage.removeItem(TEAM_ID_KEY);
          sessionStorage.removeItem(CONTEXT_KEY);
          toast.success(t('slackOAuthCallback.channelConnected', { channelName: data.channelName }), { position: TOAST_POSITION });

          navigate(backUrl);
        },
        onError: () => {
          toast.error(t('slackOAuthCallback.couldNotInstall'), { position: TOAST_POSITION });
        },
      }
    );
  };

  const publicChannels = channels.filter(c => !c.isPrivate);
  const privateChannels = channels.filter(c => c.isPrivate);

  const renderChannelOptions = () => (
    <>
      <option value="">{t('slackOAuthCallback.selectChannelOption')}</option>
      {publicChannels.length > 0 && (
        <optgroup label={t('slackOAuthCallback.publicChannels')}>
          {publicChannels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              #{channel.name}
            </option>
          ))}
        </optgroup>
      )}
      {privateChannels.length > 0 && (
        <optgroup label={t('slackOAuthCallback.privateChannels')}>
          {privateChannels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              🔒 {channel.name}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );

  const loadingContent = (
    <div className={classes.centerContainer}>
      <PageLoader />
      <Paragraph size="lg">{t('slackOAuthCallback.connectingToSlack')}</Paragraph>
    </div>
  );

  if (isLoading) {
    return <PageLayout>{loadingContent}</PageLayout>;
  }

  if (callbackError) {
    const errorContent = (
      <div className={classes.container}>
        <ContainerCard>
          <ContainerCard.Body>
            <Alert variant="danger" title={t('slackOAuthCallback.somethingWentWrong')} size="lg">
              <Paragraph size="md">{t('slackOAuthCallback.couldNotConnect')}</Paragraph>
            </Alert>
            <div className={classes.buttonGroup}>
              <Button variant="primary" onClick={() => navigate(backUrl)}>
                {t('slackOAuthCallback.back')}
              </Button>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    );

    return <PageLayout>{errorContent}</PageLayout>;
  }


  const settingsContent = (
    <div className={classes.container}>
      <ContainerCard>
        <ContainerCard.Title>{t('slackOAuthCallback.connectToSlack')}</ContainerCard.Title>
        <ContainerCard.Body>
          <Paragraph size="md" className={classes.intro}>
            {t('slackOAuthCallback.selectChannel')} {workspaceName}.
          </Paragraph>

          <div className={classes.formGroup}>
            <Label label={t('slackOAuthCallback.channel')} htmlFor="channelSelect" size="ml" required />
            <select
              id="channelSelect"
              className={classes.select}
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
            >
              {renderChannelOptions()}
            </select>
          </div>

          <div className={classes.buttonGroup}>
            <Button
              variant="primary"
              onClick={handleInstall}
              disabled={!selectedChannel || !workspaceId || !teamId}
              loading={isInstalling}
              size="large"
            >
              {t('slackOAuthCallback.connectButton')}
            </Button>
            <Button variant="secondary" onClick={() => navigate(backUrl)} size="large">
              {t('slackOAuthCallback.cancel')}
            </Button>
          </div>
        </ContainerCard.Body>
      </ContainerCard>
    </div>
  );


  return <PageLayout>{settingsContent}</PageLayout>;
};
